import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import loreCornerDetail from '../../assets/lore/lore_details.png';
import loreButton from '../../assets/lore/lore_button.png';

function tierTitle(tier) {
  if (tier === 'rare') return 'Rare Shrine';
  if (tier === 'mythic') return 'Mythic Shrine';
  return 'Common Shrine';
}

function CornerDetail({ className = '', rotationClass = '' }) {
  return (
    <img
      src={loreCornerDetail}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`pointer-events-none absolute h-[52px] w-[52px] select-none opacity-80 ${rotationClass} ${className}`}
    />
  );
}

function ActionButton({ label, onClick, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="relative h-[68px] w-[220px] appearance-none border-0 bg-transparent p-0 text-white shadow-none outline-none transition hover:opacity-90 active:scale-[0.99] disabled:cursor-default disabled:opacity-45 sm:w-[250px]"
      style={{ fontFamily: 'Karma, serif' }}
    >
      <img
        src={loreButton}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
      />
      <span className="relative z-[1] flex h-full w-full items-center justify-center px-5 text-center text-[17px] font-semibold leading-[1.05] translate-y-[2px] sm:text-[18px]">
        {label}
      </span>
    </button>
  );
}

export function ShrineModal({
  open,
  tier,
  costText,
  rewardText,
  canAfford,
  deckCards,
  onConfirm,
  onClose,
  error,
  resultText,
}) {
  const [selectedCardId, setSelectedCardId] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setSelectedCardId(null);
  }, [open, tier]);

  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const isMythic = tier === 'mythic';
  const canConfirm = Boolean(canAfford) && (!isMythic || Boolean(selectedCardId));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[12000] flex items-center justify-center overflow-hidden p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={() => onClose?.()}
        >
          <motion.div
            className={`relative z-[1] max-w-[94vw] bg-black/80 px-6 pb-8 pt-5 text-white shadow-[0_20px_60px_rgba(0,0,0,0.72)] sm:px-8 sm:pb-10 sm:pt-6 ${
              isMythic ? 'w-[860px]' : 'w-[620px]'
            }`}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ fontFamily: 'Karma, serif' }}
          >
            <CornerDetail className="left-4 top-4" rotationClass="rotate-180" />
            <CornerDetail className="right-4 top-4" rotationClass="-rotate-90" />
            <CornerDetail className="left-4 bottom-4" rotationClass="rotate-90" />
            <CornerDetail className="right-4 bottom-4" />

            <div
              className={`relative z-[1] mx-auto flex max-h-[82svh] w-full flex-col items-center pt-6 sm:pt-8 ${
                isMythic ? 'max-w-[760px]' : 'max-w-[520px]'
              }`}
            >
              <h2 className="text-center text-[18px] font-semibold text-white sm:text-[20px]">
                {tierTitle(tier)}
              </h2>

              {resultText ? (
                <>
                  <div
                    className="mt-5 min-h-0 w-full overflow-y-auto pr-1 text-center text-[18px] leading-[1.35] whitespace-pre-wrap text-white/90"
                    style={{ touchAction: 'pan-y' }}
                    onWheelCapture={(e) => e.stopPropagation()}
                    onTouchStartCapture={(e) => e.stopPropagation()}
                    onTouchMoveCapture={(e) => e.stopPropagation()}
                  >
                    {resultText}
                  </div>

                  <div className="mt-6 flex w-full justify-center">
                    <ActionButton label="Continue" onClick={onClose} />
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="mt-5 min-h-0 w-full flex-1 overflow-y-auto pr-1 overscroll-contain"
                    style={{ touchAction: 'pan-y' }}
                    onWheelCapture={(e) => e.stopPropagation()}
                    onTouchStartCapture={(e) => e.stopPropagation()}
                    onTouchMoveCapture={(e) => e.stopPropagation()}
                  >
                    <div className="space-y-4">
                      <div>
                        <div className="text-[14px] uppercase tracking-widest text-white/60">
                          Sacrifice
                        </div>
                        <div className="mt-2 text-[18px] leading-[1.25] text-white">
                          {costText}
                        </div>
                      </div>

                      <div className="pt-1">
                        <div className="text-[14px] uppercase tracking-widest text-white/60">
                          Reward
                        </div>
                        <div className="mt-2 text-[18px] leading-[1.25] text-white/90">
                          {rewardText}
                        </div>
                      </div>

                      {isMythic && (
                        <div className="pt-2">
                          <div className="mb-3 text-[14px] uppercase tracking-widest text-white/60">
                            Choose a card to sacrifice
                          </div>

                          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                            {(Array.isArray(deckCards) ? deckCards : []).map((c) => {
                              const id = c?.id;
                              if (!id) return null;
                              const selected = selectedCardId === id;

                              return (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => setSelectedCardId(id)}
                                  className={[
                                    'rounded-[16px] border bg-black/90 p-2 text-left transition',
                                    selected
                                      ? 'border-white/35 bg-white/[0.04]'
                                      : 'border-white/10 hover:bg-white/[0.04]',
                                  ].join(' ')}
                                >
                                  {c?.img ? (
                                    <div className="overflow-hidden rounded-[10px] bg-black/40">
                                      <img
                                        src={c.img}
                                        alt={c.title || ''}
                                        className="w-full h-auto rounded-[10px]"
                                        draggable={false}
                                      />
                                    </div>
                                  ) : (
                                    <div className="flex aspect-[3/4] w-full items-center justify-center rounded-[10px] bg-white/5 text-white/70">
                                      No image
                                    </div>
                                  )}

                                  <div className="mt-2 truncate text-sm font-semibold leading-tight text-white">
                                    {c?.title || 'Unknown Card'}
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          <div className="mt-3 text-center text-xs text-white/60">
                            The chosen card will be removed from your deck permanently.
                          </div>
                        </div>
                      )}

                      {!canAfford && (
                        <div className="rounded-xl border border-red-500/40 bg-red-900/20 p-3 text-sm text-red-200">
                          You cannot afford this sacrifice.
                        </div>
                      )}

                      {error && (
                        <div className="rounded-xl border border-red-500/40 bg-red-900/20 p-3 text-sm text-red-200">
                          {error}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center">
                    <ActionButton
                      label="Offer"
                      onClick={() => onConfirm?.({ selectedCardId })}
                      disabled={!canConfirm}
                    />
                    <ActionButton label="Leave" onClick={onClose} />
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
